import { useCallback, useEffect, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { useGenericBi } from './use-generic-bi';
import {
  biBucket,
  cancelHeldSendWithRetry,
  parseHeldSendsList,
  SEND_HOLD_BI_PAGE,
  sendHoldCancelOutcome,
  sendHoldIsFinal,
  sendHoldReduce,
  sendHoldSendNowPhase,
  sendHoldSentNowFireAt,
  type HeldSendsList,
  type SendHoldBucket,
  type SendHoldCancelResponse,
  type SendHoldLiveState,
  type SendHoldPhase,
  type SendHoldSurface,
  type SendNowResponse,
} from './send-hold';

/** The client waits at least as long as the route waits for the bff (25s), which waits for the agent's wake. */
export const SEND_HOLD_CANCEL_TIMEOUT_MS = 30_000;
/** The client waits a little longer than the route waits for the bff (15s). Send now is never retried. */
export const SEND_HOLD_SEND_NOW_TIMEOUT_MS = 20_000;

/**
 * Whose live list a surface reads: a chat chip by its conversation, the task section by its task, and a meeting
 * summary's card BY ITS HOLD IDS (never by the summary row's conversation — a meeting hold belongs to the brain chat).
 */
export type HeldSendsScope = { conversationId: string } | { taskId: string } | { ids: readonly string[] };

/** The `/api/held-sends` query for a scope. `ids` are sorted (one cache entry per set) and capped at 10. */
export function heldSendsQueryString(scope: HeldSendsScope): string {
  if ('ids' in scope) return new URLSearchParams({ ids: [...new Set(scope.ids)].sort().slice(0, 10).join(',') }).toString();
  return new URLSearchParams('conversationId' in scope ? { conversationId: scope.conversationId } : { taskId: scope.taskId }).toString();
}

/**
 * The page's MONOTONIC clock, for the "first seen" and "read started" stamps that are only ever compared with each
 * other: a wall-clock step (NTP, the owner changing the time) between the two must not reorder them, or a read made
 * before a hold existed could end it. `performance.now()` is per page, which is exactly the life of both stamps.
 */
export function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

/** One live read, stamped (on `monotonicNow`) with when it STARTED — a hold first seen after that is not covered by it. */
export type HeldSendsRead = { list: HeldSendsList; startedAt: number };

export async function fetchHeldSends(scope: HeldSendsScope): Promise<HeldSendsRead> {
  const startedAt = monotonicNow();
  const res = await apiFetch(`${API_BASE}/api/held-sends?${heldSendsQueryString(scope)}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`held-sends ${res.status}`);
  const list = parseHeldSendsList(await res.json());
  if (!list) throw new Error('held-sends: unreadable body');
  return { list, startedAt };
}

function scopeKey(scope: HeldSendsScope) {
  if ('ids' in scope) return queryKeys.heldSends('ids', heldSendsQueryString(scope));
  return 'conversationId' in scope ? queryKeys.heldSends('conversation', scope.conversationId) : queryKeys.heldSends('task', scope.taskId);
}

function scopeId(scope: HeldSendsScope | undefined): string {
  if (!scope) return '';
  if ('ids' in scope) return `i:${heldSendsQueryString(scope)}`;
  return 'conversationId' in scope ? `c:${scope.conversationId}` : `t:${scope.taskId}`;
}

/**
 * The live list for one conversation or one task.
 * - `list` is `undefined` before the first read and `null` while the newest read FAILED — and a failed read keeps
 *   every Cancel (`sendHoldLiveState` reads it as unknown).
 * - `lastGood` is the newest successful list, kept through a failed refetch (the task section keeps its rows).
 * - `readStartedAt` is when the read behind `list` started: a hold first seen after it is not covered by it
 *   (`sendHoldCoveredLiveState`).
 */
export function useHeldSends(scope: HeldSendsScope | undefined, enabled = true) {
  const id = scopeId(scope);
  const query = useQuery({
    queryKey: scope ? scopeKey(scope) : ['held-sends', 'none'],
    queryFn: () => fetchHeldSends(scope as HeldSendsScope),
    enabled: Boolean(scope) && enabled,
    staleTime: 15_000,
  });
  const failed = query.isError;
  const list: HeldSendsList | null | undefined = failed ? null : query.data?.list;
  const refetch = useCallback(() => {
    if (scope) void queryClient.invalidateQueries({ queryKey: scopeKey(scope) });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return { list, lastGood: query.data?.list, readStartedAt: failed ? undefined : query.data?.startedAt, fetching: query.isFetching, refetch };
}

/** When this page first saw each hold (on `monotonicNow`) — the yardstick a live read must start after to speak for it. */
const holdFirstSeen = new Map<string, number>();
export function noteHoldSeen(holdId: string): number {
  let at = holdFirstSeen.get(holdId);
  if (at === undefined) {
    at = monotonicNow();
    holdFirstSeen.set(holdId, at);
  }
  return at;
}

/** POST the Cancel. Resolves the contract response; a timeout, a non-200 or an unreadable body is ERROR. */
export async function postCancelHeldSend(heldSendId: string, surface: SendHoldSurface): Promise<SendHoldCancelResponse> {
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timer = setTimeout(() => controller?.abort(), SEND_HOLD_CANCEL_TIMEOUT_MS);
  try {
    const res = await apiFetch(`${API_BASE}/api/held-sends/${encodeURIComponent(heldSendId)}/cancel`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ surface }),
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!res.ok) return 'ERROR';
    const outcome = ((await res.json()) as { outcome?: unknown })?.outcome;
    return outcome === 'CANCELED' || outcome === 'TOO_LATE' || outcome === 'NOT_FOUND' ? outcome : 'ERROR';
  } catch {
    return 'ERROR';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * POST Send now. Resolves the route's answer, or undefined when there is none (a timeout, a non-200, an unreadable
 * body) — which reads as "Couldn't send now". Called ONCE: Send now fails toward not sending and is never retried.
 */
export async function postSendNowHeldSend(heldSendId: string, surface: SendHoldSurface): Promise<SendNowResponse | undefined> {
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timer = setTimeout(() => controller?.abort(), SEND_HOLD_SEND_NOW_TIMEOUT_MS);
  try {
    const res = await apiFetch(`${API_BASE}/api/held-sends/${encodeURIComponent(heldSendId)}/send-now`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ surface }),
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!res.ok) return undefined;
    const body = (await res.json()) as SendNowResponse | null;
    return body && typeof body === 'object' ? body : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/** A server's FINAL answer (Cancel or Send now). A failure, an in-flight call or "Sending…" is never one. */
export type HoldFinalPhase = Extract<SendHoldPhase, 'canceled' | 'tooLate' | 'ended' | 'sentNow' | 'sendNowCanceled'>;

/**
 * The page's record of each hold's FINAL Cancel answer, subscribable per hold id: every mounted row for a hold (the
 * chat chip and the task drill-in at once, or a row that remounted while its call was out) re-renders when the answer
 * lands. The twin of iOS `SendHoldRegistry` and Android `SendHoldController`, with the SAME rule on all three: the
 * first final answer stands — a later final does not replace it, and a failure is never recorded at all.
 */
function createHoldOutcomes() {
  const phases = new Map<string, HoldFinalPhase>();
  const listeners = new Map<string, Set<() => void>>();
  return {
    get: (holdId: string): HoldFinalPhase | undefined => phases.get(holdId),
    /** Records a final answer unless one is already held. A non-final phase is ignored. */
    record(holdId: string, phase: SendHoldPhase): void {
      if (!sendHoldIsFinal(phase) || phases.has(holdId)) return;
      phases.set(holdId, phase as HoldFinalPhase);
      listeners.get(holdId)?.forEach((notify) => notify());
    },
    subscribe(holdId: string, notify: () => void): () => void {
      let set = listeners.get(holdId);
      if (!set) listeners.set(holdId, (set = new Set()));
      set.add(notify);
      return () => {
        set?.delete(notify);
        if (set?.size === 0) listeners.delete(holdId);
      };
    },
  };
}
export const holdOutcomes = createHoldOutcomes();

/**
 * The holds with a Cancel or Send now call out, page-wide: one call per hold at a time, from whichever row — a second
 * row for the same hold (the summary card and the task drill-in at once) cannot start another while it is out. Mirrors
 * iOS `SendHoldRegistry` and Android `SendHoldController`.
 */
const callsOut = new Map<string, number>();
/** The holds with a Send now out: a Cancel from another row waits for it (both buttons read disabled on the asker). */
const sendNowInFlight = new Set<string>();
/** Per hold, the rows that draw what is out (`useHoldCallsOut`): every row re-renders when a call starts or lands. */
const callListeners = new Map<string, Set<() => void>>();
const notifyCalls = (holdId: string) => callListeners.get(holdId)?.forEach((notify) => notify());
export const holdCallsInFlight = {
  has: (holdId: string) => (callsOut.get(holdId) ?? 0) > 0,
  add: (holdId: string) => {
    callsOut.set(holdId, (callsOut.get(holdId) ?? 0) + 1);
    notifyCalls(holdId);
  },
  delete: (holdId: string) => {
    const n = (callsOut.get(holdId) ?? 0) - 1;
    if (n > 0) callsOut.set(holdId, n);
    else callsOut.delete(holdId);
    notifyCalls(holdId);
  },
};
const sendNowCalls = {
  add: (holdId: string) => {
    sendNowInFlight.add(holdId);
    notifyCalls(holdId);
  },
  delete: (holdId: string) => {
    sendNowInFlight.delete(holdId);
    notifyCalls(holdId);
  },
};

export type HoldCallsOut = 'none' | 'cancel' | 'sendNow';
function holdCallsOut(holdId: string): HoldCallsOut {
  if (sendNowInFlight.has(holdId)) return 'sendNow';
  return holdCallsInFlight.has(holdId) ? 'cancel' : 'none';
}

/**
 * What is out for one hold from ANY row, live — so a row draws the buttons another row's call would make it ignore as
 * disabled (M2) rather than offering a tap that silently does nothing: any call out disables Send now, and a Send now
 * out disables Cancel too. The twin of iOS `SendHoldRegistry.callOut` and Android `SendHoldController.callOut`.
 */
export function useHoldCallsOut(holdId: string): HoldCallsOut {
  const subscribe = useCallback(
    (notify: () => void) => {
      let set = callListeners.get(holdId);
      if (!set) callListeners.set(holdId, (set = new Set()));
      set.add(notify);
      return () => {
        set?.delete(notify);
        if (set?.size === 0) callListeners.delete(holdId);
      };
    },
    [holdId],
  );
  const read = useCallback(() => holdCallsOut(holdId), [holdId]);
  return useSyncExternalStore(subscribe, read, read);
}

export function useHoldOutcome(holdId: string): HoldFinalPhase | undefined {
  const subscribe = useCallback((notify: () => void) => holdOutcomes.subscribe(holdId, notify), [holdId]);
  const read = useCallback(() => holdOutcomes.get(holdId), [holdId]);
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * One hold's Cancel: the shared reducer (`sendHoldReduce`), the one-retry call, the BI after the final answer, and
 * the live read folded in. NEVER a chat send and never gated on a turn, the channel or credits — so it works while a
 * reply is streaming. `cancel` is injectable for tests.
 */
export function useHoldCancel({
  holdId,
  fireAt,
  bucket,
  surface,
  live,
  onSettled,
  cancel = postCancelHeldSend,
  sendNow = postSendNowHeldSend,
}: {
  holdId: string;
  fireAt: Date;
  bucket?: SendHoldBucket;
  surface: SendHoldSurface;
  live: SendHoldLiveState;
  onSettled?: (phase: SendHoldPhase) => void;
  cancel?: (heldSendId: string, surface: SendHoldSurface) => Promise<SendHoldCancelResponse>;
  sendNow?: (heldSendId: string, surface: SendHoldSurface) => Promise<SendNowResponse | undefined>;
}) {
  // The page's record of SERVER answers wins over this row's own phase: a row that remounts (a re-keyed transcript), the
  // same hold on the task screen, or a tap whose answer landed after the row left must never show a live Cancel for a
  // hold the server already answered — and it updates the moment the answer lands, from whichever row asked. Only a
  // Cancel answer is recorded; a live read's `ended` never is.
  const recorded = useHoldOutcome(holdId);
  const [local, dispatch] = useReducer(sendHoldReduce, 'idle' as SendHoldPhase);
  const phase: SendHoldPhase = recorded ?? local;
  const { logPageClickBi } = useGenericBi();
  const inFlight = useRef(false);
  // `onSettled` fires ONCE per row per hold for a final answer, whichever row asked: callers refresh there (the stage
  // card re-reads the contact book, the task section its list), so a row that only RECEIVED the answer through the
  // store must behave like the row that made the call. An answer already recorded when the row mounted is old news
  // and fires nothing. A failure (`cancelFailed`) is never shared, so only the calling row reports it.
  const settled = useRef<{ holdId: string; atMount: SendHoldPhase | undefined; fired: boolean }>({
    holdId,
    atMount: holdOutcomes.get(holdId),
    fired: false,
  });
  if (settled.current.holdId !== holdId) settled.current = { holdId, atMount: holdOutcomes.get(holdId), fired: false };
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const settleOnce = useCallback(
    (forHold: string, final: SendHoldPhase) => {
      const mark = settled.current;
      if (mark.holdId !== forHold || mark.fired) return;
      mark.fired = true;
      onSettledRef.current?.(final);
    },
    [],
  );

  useEffect(() => {
    dispatch({ type: 'live', state: live });
  }, [live]);
  // A live `ended` is re-applied whenever this row's phase moves, not only when the verdict changes: one that arrived
  // while a call was out (ignored then) ends the row the moment the call lands on a phase it may end — a failed Send
  // now included — as on iOS and Android, which fold the verdict in at every read (M5).
  useEffect(() => {
    if (live === 'ended') dispatch({ type: 'live', state: 'ended' });
  }, [live, local]);

  useEffect(() => {
    if (recorded !== undefined && settled.current.atMount === undefined) settleOnce(holdId, recorded);
  }, [holdId, recorded, settleOnce]);

  // Send now's answer: the `{time}` of "It will go at …" and, once accepted, the row's moved send time.
  const [sendNowAnswer, setSendNowAnswer] = useState<{ sendAt?: string; fireAt?: Date } | undefined>(undefined);

  /**
   * Send now (meeting hold): ONE direct call — never a chat send, never retried (it fails toward not sending), and
   * ignored while a Cancel or another Send now for this row is out. Its BI fires once, after the answer.
   */
  const onSendNow = useCallback(async () => {
    if (inFlight.current || holdCallsInFlight.has(holdId)) return;
    inFlight.current = true;
    holdCallsInFlight.add(holdId);
    sendNowCalls.add(holdId);
    dispatch({ type: 'sendNowTap' });
    let response: SendNowResponse | undefined;
    try {
      response = await sendNow(holdId, surface);
    } catch {
      response = undefined;
    }
    const landed = sendHoldSendNowPhase(response);
    const sendAt = typeof response?.sendAt === 'string' ? response.sendAt : undefined;
    setSendNowAnswer({ sendAt, ...(landed === 'sendNowSending' ? { fireAt: sendHoldSentNowFireAt(fireAt, sendAt, new Date()) } : {}) });
    holdOutcomes.record(holdId, landed);
    dispatch({ type: 'sendNowOutcome', phase: landed });
    inFlight.current = false;
    holdCallsInFlight.delete(holdId);
    sendNowCalls.delete(holdId);
    logPageClickBi(
      { page_name: SEND_HOLD_BI_PAGE[surface], button_name: 'send_hold_send_now' },
      { componentName: 'send-hold-send-now', additionalFields: { surface, outcome: landed } },
    );
    if (sendHoldIsFinal(landed)) settleOnce(holdId, holdOutcomes.get(holdId) ?? landed);
    else onSettledRef.current?.(landed);
  }, [holdId, fireAt, surface, sendNow, logPageClickBi, settleOnce]);

  const onCancel = useCallback(async () => {
    // A Cancel is never refused for another row's call: it fails toward cancelling (and is idempotent). Only a Send
    // now in flight for this hold, from any row, holds it back.
    if (inFlight.current || sendNowInFlight.has(holdId)) return;
    inFlight.current = true;
    holdCallsInFlight.add(holdId);
    dispatch({ type: 'tap' });
    const response = await cancelHeldSendWithRetry(() => cancel(holdId, surface));
    const outcome = sendHoldCancelOutcome(response, fireAt, new Date());
    // Recorded before the dispatch, so the answer survives a row that unmounted while the call was out, and reaches
    // every other row for this hold. A failure is not recorded, so it never displaces another row's final answer.
    if (!outcome.keepButton) holdOutcomes.record(holdId, outcome.outcome);
    dispatch({ type: 'outcome', outcome });
    inFlight.current = false;
    holdCallsInFlight.delete(holdId);
    logPageClickBi(
      { page_name: SEND_HOLD_BI_PAGE[surface], button_name: 'send_hold_cancel' },
      { componentName: 'send-hold-cancel', additionalFields: { surface, bucket: biBucket(bucket), outcome: outcome.outcome } },
    );
    if (outcome.keepButton) onSettledRef.current?.('cancelFailed');
    // The hold's answer is the FIRST final recorded (another row's may have landed while this call was out). Called
    // here too, not only from the store's effect, so a row that unmounted mid-call still reports it.
    else settleOnce(holdId, holdOutcomes.get(holdId) ?? outcome.outcome);
  }, [holdId, fireAt, bucket, surface, cancel, logPageClickBi, settleOnce]);

  return { phase, onCancel, onSendNow, sendNowAnswer };
}

/** True while the element is on screen (the hold's clock runs only then — native rule 14e, the same on web). */
export function useOnScreen<T extends Element>(): [(node: T | null) => void, boolean] {
  const [node, setNode] = useState<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!node) return;
    if (typeof IntersectionObserver !== 'function') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => setVisible(entries.some((e) => e.isIntersecting)));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, visible];
}
